'use client'

import { useState, useEffect } from 'react'
import { History, CheckCircle, XCircle, Download, Eye, X, RefreshCw, AlertTriangle } from 'lucide-react'
import api from '@/lib/platform-api'
import { useNotification } from '@/context/NotificationContext'
import { useRequireModule } from '@/hooks/useRequireModule'

interface Transaction {
  transaction_id?: string
  id?: string | number
  collection_name?: string
  file_name?: string
  original_filename?: string
  status?: string
  created_at?: string
  row_count?: number
  error_message?: string
  columns?: string[]
}

interface UploadFailure {
  id?: string | number
  file_name?: string
  original_filename?: string
  error_message?: string
  created_at?: string
}

interface DeltaUpdate {
  task_id: string
  collection_name?: string
  file_name?: string
  method?: string
  status?: string
  progress?: number
  error?: string
  result?: {
    added?: number
    updated?: number
    failed?: number
    failure_count?: number
    success_count?: number
    total?: number
    total_records?: number
    processed_records?: number
    failed_rows?: { row_index: number; error: string }[]
  }
  created_at?: string
  completed_at?: string
}

export default function UploadHistoryPage() {
  const { ready } = useRequireModule('module_upload')
  const { addNotification } = useNotification()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [failures, setFailures] = useState<UploadFailure[]>([])
  const [deltas, setDeltas] = useState<DeltaUpdate[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'successful' | 'delta' | 'failed'>('successful')
  const [selected, setSelected] = useState<Transaction | null>(null)

  const formatDate = (d?: string) => {
    if (!d) return 'N/A'
    return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [txRes, failRes, deltaRes] = await Promise.all([
          api.get('/user-transactions/'),
          api.get('/upload-failures/'),
          api.get('/user-delta-history/'),
        ])
        if (txRes.data.success) setTransactions(txRes.data.transactions || [])
        setFailures(Array.isArray(failRes.data) ? failRes.data : (failRes.data?.failures || []))
        if (deltaRes.data.success) setDeltas(deltaRes.data.delta_updates || [])
      } catch {
        addNotification({ type: 'error', title: 'Error', message: 'Failed to load upload history' })
      } finally { setLoading(false) }
    }
    load()
  }, []) // eslint-disable-line

  const handleDownload = async (txId?: string) => {
    if (!txId) return
    try {
      const res = await api.get(`/download-file/${txId}`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(res.data)
      const a = document.createElement('a'); a.href = url; a.download = `${txId}.csv`; a.click()
      window.URL.revokeObjectURL(url)
    } catch { addNotification({ type: 'error', title: 'Error', message: 'Failed to download file' }) }
  }

  if (!ready) return null

  // Hide delta runs where every row failed (zero successes) from the view.
  // The records are NOT deleted — they remain in the database / API response.
  const visibleDeltas = deltas.filter(d => {
    const r = d.result
    if (!r) return true
    const failed = r.failure_count ?? r.failed ?? 0
    const succeeded = r.success_count ?? (r.added ?? 0) + (r.updated ?? 0)
    return !(failed > 0 && succeeded === 0)
  })

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-white shadow-lg">
            <History size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Upload History</h1>
            <p className="text-slate-500 text-sm">Track your file uploads and view upload history</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-xl border border-slate-200 p-1 w-fit">
          {(['successful', 'delta', 'failed'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${tab === t ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {t === 'successful' ? <span className="flex items-center gap-1.5"><CheckCircle size={14} /> Uploads ({transactions.length})</span>
                : t === 'delta' ? <span className="flex items-center gap-1.5"><RefreshCw size={14} /> Delta Updates ({visibleDeltas.length})</span>
                : <span className="flex items-center gap-1.5"><XCircle size={14} /> Failed ({failures.length})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400">Loading…</div>
        ) : tab === 'successful' ? (
          transactions.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <History size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No uploads yet</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Collection</th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden md:table-cell">File</th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:table-cell">Date</th>
                    <th className="text-right py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Rows</th>
                    <th className="py-3 px-5"></th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, i) => (
                    <tr key={tx.transaction_id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-5">
                        <span className="font-medium text-slate-700">{tx.collection_name || '—'}</span>
                      </td>
                      <td className="py-3.5 px-5 hidden md:table-cell text-slate-500">{tx.original_filename || tx.file_name || '—'}</td>
                      <td className="py-3.5 px-5 hidden sm:table-cell text-slate-400 text-xs">{formatDate(tx.created_at)}</td>
                      <td className="py-3.5 px-5 text-right">
                        {tx.row_count !== undefined && <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold">{tx.row_count.toLocaleString()}</span>}
                      </td>
                      <td className="py-3.5 px-5">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => setSelected(tx)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"><Eye size={15} /></button>
                          <button onClick={() => handleDownload(tx.transaction_id)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"><Download size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : tab === 'delta' ? (
          visibleDeltas.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <RefreshCw size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No delta updates yet</p>
            </div>
          ) : (
            <>
              {(() => {
                const totals = visibleDeltas.reduce(
                  (acc, d) => {
                    const r = d.result
                    if (!r) return acc
                    const added = r.added ?? 0
                    const updated = r.updated ?? 0
                    const failed = r.failure_count ?? r.failed ?? 0
                    const succeeded = r.success_count ?? added + updated
                    const uploaded = r.total_records ?? succeeded + failed
                    acc.uploaded += uploaded
                    acc.succeeded += succeeded
                    acc.failed += failed
                    acc.added += added
                    acc.updated += updated
                    return acc
                  },
                  { uploaded: 0, succeeded: 0, failed: 0, added: 0, updated: 0 },
                )
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Rows uploaded</p>
                      <p className="text-2xl font-bold text-slate-800 mt-1">{totals.uploaded.toLocaleString()}</p>
                      <p className="text-xs text-slate-400 mt-0.5">across {visibleDeltas.length} runs</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Successful</p>
                      <p className="text-2xl font-bold text-emerald-600 mt-1">{totals.succeeded.toLocaleString()}</p>
                      <p className="text-xs text-slate-400 mt-0.5">+{totals.added.toLocaleString()} added · {totals.updated.toLocaleString()} updated</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Failed</p>
                      <p className="text-2xl font-bold text-red-600 mt-1">{totals.failed.toLocaleString()}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{totals.uploaded > 0 ? ((totals.failed / totals.uploaded) * 100).toFixed(1) : '0'}% of rows</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Success rate</p>
                      <p className="text-2xl font-bold text-slate-800 mt-1">{totals.uploaded > 0 ? ((totals.succeeded / totals.uploaded) * 100).toFixed(1) : '0'}%</p>
                      <p className="text-xs text-slate-400 mt-0.5">{totals.succeeded.toLocaleString()} / {totals.uploaded.toLocaleString()}</p>
                    </div>
                  </div>
                )
              })()}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Collection</th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden md:table-cell">File</th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:table-cell">Mode</th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:table-cell">Date</th>
                    <th className="text-right py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Uploaded</th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="text-right py-3 px-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDeltas.map((d) => (
                    <tr key={d.task_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-5">
                        <span className="font-medium text-slate-700">{d.collection_name || '—'}</span>
                      </td>
                      <td className="py-3.5 px-5 hidden md:table-cell text-slate-500 truncate max-w-[200px]">{d.file_name || '—'}</td>
                      <td className="py-3.5 px-5 hidden sm:table-cell">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${d.method === 'upsert' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                          {d.method || 'add'}
                        </span>
                      </td>
                      <td className="py-3.5 px-5 hidden sm:table-cell text-slate-400 text-xs">{formatDate(d.created_at)}</td>
                      <td className="py-3.5 px-5 text-right">
                        {(() => {
                          const r = d.result
                          const uploaded = r
                            ? r.total_records ?? (r.success_count ?? (r.added ?? 0) + (r.updated ?? 0)) + (r.failure_count ?? r.failed ?? 0)
                            : null
                          return uploaded != null
                            ? <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">{uploaded.toLocaleString()}</span>
                            : <span className="text-xs text-slate-400">—</span>
                        })()}
                      </td>
                      <td className="py-3.5 px-5">
                        {d.status === 'completed' ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle size={13} /> Done</span>
                        ) : d.status === 'partial_success' ? (
                          <span className="flex items-center text-amber-600" title="Completed with some row failures"><AlertTriangle size={14} /></span>
                        ) : d.status === 'failed' ? (
                          <span className="flex items-center gap-1 text-red-500 text-xs font-semibold" title={d.error || ''}><XCircle size={13} /> Failed</span>
                        ) : (
                          <span className="text-amber-500 text-xs font-semibold">{d.progress?.toFixed(0) ?? 0}%</span>
                        )}
                      </td>
                      <td className="py-3.5 px-5 text-right">
                        {d.result ? (
                          <div className="flex items-center gap-2 justify-end text-xs">
                            {d.result.added != null && <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-bold">+{d.result.added}</span>}
                            {d.result.updated != null && <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-bold">{d.result.updated} updated</span>}
                            {(() => {
                              const failedCount = d.result.failure_count ?? d.result.failed ?? 0
                              if (failedCount <= 0) return null
                              const firstError = d.result.failed_rows?.[0]?.error
                              return (
                                <span
                                  className="px-2 py-0.5 rounded-md bg-red-50 text-red-600 font-bold cursor-help"
                                  title={firstError ? `First error: ${firstError}` : 'Some rows failed'}
                                >
                                  {failedCount} failed
                                </span>
                              )
                            })()}
                          </div>
                        ) : d.status === 'failed' ? (
                          <span className="text-xs text-red-400 truncate max-w-[150px] inline-block">{d.error || ''}</span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )
        ) : (
          failures.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <CheckCircle size={40} className="mx-auto text-emerald-300 mb-3" />
              <p className="text-slate-500 font-medium">No failed uploads</p>
            </div>
          ) : (
            <div className="space-y-3">
              {failures.map((f, i) => (
                <div key={f.id || i} className="bg-white border border-red-100 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <XCircle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-700">{f.original_filename || f.file_name || 'Unknown file'}</p>
                      <p className="text-sm text-red-500 mt-1">{f.error_message || 'Unknown error'}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatDate(f.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Details Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-slate-800">Upload Details</h3>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors"><X size={16} /></button>
            </div>
            <dl className="space-y-3 text-sm">
              {[
                ['Collection', selected.collection_name],
                ['File', selected.original_filename || selected.file_name],
                ['Date', formatDate(selected.created_at)],
                ['Rows', selected.row_count?.toLocaleString()],
                ['Transaction ID', selected.transaction_id],
                ['Columns', Array.isArray(selected.columns) ? selected.columns.join(', ') : selected.columns],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k as string} className="flex gap-3">
                  <dt className="w-32 text-slate-400 flex-shrink-0">{k}</dt>
                  <dd className="text-slate-700 break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  )
}
